import { memo, useState } from "react"
import { Layers } from "lucide-react"
import { FACEBOOK_FEED_ASPECT_CLASS, Icon, MediaPreview, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { humanizeBrandName } from "../../../lib/format-opportunity"
import type { CreativeExample } from "../../../types/opportunities.types"
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions"
import { CreateFlagDialog } from "../CreateFlagDialog/CreateFlagDialog"
import { ViewFlagsDialog } from "../ViewFlagsDialog/ViewFlagsDialog"
import { opportunityKeys, creativeFlagKeys } from "@/lib/queries"
import type { CreativeCardContext } from "../OpportunityCreatives/VirtualizedCreativeGrid"
import { PAGE_SIZE } from "../OpportunityCreatives/OpportunityCreatives"

export interface CreativeCardProps {
  creative: CreativeExample
  cardContext?: CreativeCardContext
}

// Built once at module load. `new Intl.DateTimeFormat(...)` is expensive and was
// previously reconstructed on every render of every card — a measurable scripting
// cost in the virtualized grid, where cards re-render on each scroll tick (AD-1569).
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" })

function formatDate(iso: string): string {
  return DATE_FORMAT.format(new Date(iso))
}

function CreativeCardImpl({ creative, cardContext }: CreativeCardProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isSuperTenant = useIsSuperTenant()
  const [flagDialogOpen, setFlagDialogOpen] = useState(false)
  const [viewFlagDialogOpen, setViewFlagDialogOpen] = useState(false)
  const isCarousel = creative.objectType.toLowerCase() === "carousel"
  const isVideo = !isCarousel && creative.objectType.toLowerCase() !== "image"
  const brandName = humanizeBrandName(creative.competitor)

  const opportunityId = cardContext?.opportunityId ?? ""

  const contextLabel = cardContext?.opportunityTitle
    ? `Opportunity: ${cardContext.opportunityTitle}`
    : opportunityId
      ? `Opportunity ${opportunityId}`
      : "Opportunity"

  const opportunityIdNum = opportunityId ? Number(opportunityId) : undefined
  const numericOpportunityId = Number.isInteger(opportunityIdNum) ? opportunityIdNum : undefined

  const flagReport = (cardContext && (opportunityId || cardContext?.opportunityTitle))
    ? {
        creativeHash: creative.hash,
        ...(creative.adId ? { adId: creative.adId } : {}),
        ...(creative.competitor ? { competitor: creative.competitor } : {}),
        component: 'OPPORTUNITY' as const,
        contextLabel,
        ...(numericOpportunityId !== undefined ? { opportunityId: numericOpportunityId } : {}),
        ...(cardContext.opportunitySetId ? { opportunitySetId: cardContext.opportunitySetId } : {}),
        ...(cardContext.segment1 ? { segment1: cardContext.segment1 } : {}),
        ...(cardContext.segment2 ? { segment2: cardContext.segment2 } : {}),
        ...(cardContext.segment3 ? { segment3: cardContext.segment3 } : {}),
        ...(cardContext.feature ? { feature: cardContext.feature } : {}),
        ...(cardContext.subFeature ? { subFeature: cardContext.subFeature } : {}),
        ...(cardContext.dataset ? { dataset: cardContext.dataset, flowName: cardContext.dataset } : {}),
        ...(cardContext.engine ? { engine: cardContext.engine } : {}),
        ...(cardContext.industry ? { industry: cardContext.industry } : {}),
      }
    : null

  const lightboxMeta = (
    <>
      <span>{brandName}</span>
      <span>{creative.displayFormat}</span>
      <span>{creative.days} {t("opportunities.drawer.days")}</span>
      <span>{creative.ads} {t("opportunities.drawer.ads")}</span>
      <span>{formatDate(creative.minDate)} – {formatDate(creative.maxDate)}</span>
    </>
  )

  return (
    // content-visibility:auto lets the browser skip rendering — and reclaim decoded
    // thumbnail bitmaps for — cards scrolled out of view, bounding memory in long grids.
    // contain-intrinsic-size reserves the collapsed card's box so the scrollbar stays stable.
    <div className="group w-full overflow-hidden rounded-md border bg-card shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] transition-shadow duration-150 [content-visibility:auto] [contain-intrinsic-size:auto_360px] hover:shadow-[0_4px_8px_-2px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]">
      <MediaPreview
        src={creative.assetUrl || undefined}
        thumbnailSrc={creative.thumbnailUrl}
        type={isVideo ? "video" : "image"}
        alt={brandName}
        title={brandName}
        meta={lightboxMeta}
        slides={creative.carouselSlides}
        className={`${FACEBOOK_FEED_ASPECT_CLASS} overflow-hidden rounded-t-xl bg-muted`}
      >
        {/* Gradient for bottom strip legibility */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

        {/* Carousel / video indicator */}
        {isCarousel ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
              <Layers className="size-5" />
            </span>
          </div>
        ) : isVideo ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
            <span className="flex size-10 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
              <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </div>
        ) : null}

        {/* Bottom strip */}
        <div
          className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 px-2.5 pb-2.5"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Brand logo + name */}
          <div className="flex min-w-0 items-end gap-2">
            {creative.logoUrl ? (
              <img
                src={creative.logoUrl}
                alt={brandName}
                className="size-8 shrink-0 rounded-full object-cover ring-1 ring-white/30 backdrop-blur-sm"
                onError={(e) => {
                  e.currentTarget.style.display = "none"
                  e.currentTarget.nextElementSibling?.removeAttribute("hidden")
                }}
              />
            ) : null}
            <span
              hidden={Boolean(creative.logoUrl)}
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-[11px] font-semibold text-white ring-1 ring-white/30 backdrop-blur-sm"
            >
              {brandName.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 pb-0.5">
              <p className="truncate text-[13px] font-semibold leading-tight text-white">{brandName}</p>
              <p className="text-[10px] text-white/70">{creative.displayFormat}</p>
            </div>
          </div>

          {/* Days + ad library link */}
          <div className="flex shrink-0 items-end gap-1 pb-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-default rounded-full bg-black/40 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums leading-none text-white backdrop-blur-sm">
                  {creative.days}d
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">{t("opportunities.drawer.daysTooltip")}</TooltipContent>
            </Tooltip>
            <a
              href={creative.adLibUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md p-0.5 text-white/60 transition-colors hover:text-white"
              aria-label={t("common.openExternal")}
            >
              <Icon name="external-link" size="xs" color="inherit" />
            </a>
          </div>
        </div>
      </MediaPreview>

      {/* Carousel slide preview strip — shows additional creatives (slides 1+) */}
      {isCarousel && creative.carouselSlides && creative.carouselSlides.length > 1 ? (
        <div className="flex gap-1 overflow-x-auto px-2 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {creative.carouselSlides.slice(1).map((slide) => (
            <img
              key={slide.hash}
              src={slide.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="size-10 shrink-0 rounded object-cover"
            />
          ))}
        </div>
      ) : null}

      {/* Stats strip */}
      <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex cursor-default items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <Icon name="calendar" size="xs" color="inherit" />
              {formatDate(creative.minDate)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t("opportunities.drawer.dateRangeTooltip")}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex cursor-default items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
              <Icon name="copy" size="xs" color="inherit" />
              {creative.ads}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t("opportunities.drawer.adsTooltip")}</TooltipContent>
        </Tooltip>

        {/* Flag button — admin only */}
        {isSuperTenant && flagReport ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t("opportunities.drawer.reportBadTagging")}
                data-testid="flag-button"
                className="ml-auto rounded-md p-0.5 text-muted-foreground transition-colors hover:text-destructive"
                onClick={() => {
                  if (creative.flagCount > 0) {
                    setViewFlagDialogOpen(true)
                  } else {
                    setFlagDialogOpen(true)
                  }
                }}
              >
                <Icon
                  name="flag"
                  size="xs"
                  color="inherit"
                  className={creative.flagCount > 0 ? "fill-current" : undefined}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("opportunities.drawer.reportBadTagging")}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      {/* Create Flag Dialog — outline state (flagCount=0) */}
      {isSuperTenant && flagReport ? (
        <CreateFlagDialog
          open={flagDialogOpen}
          onOpenChange={setFlagDialogOpen}
          report={flagReport}
          onCreated={() => {
            if (opportunityId) {
              void queryClient.invalidateQueries({
                queryKey: opportunityKeys.creativesInfinite(opportunityId, PAGE_SIZE),
              })
            }
            void queryClient.invalidateQueries({
              queryKey: creativeFlagKeys.byHash(creative.hash),
            })
          }}
        />
      ) : null}

      {/* View Flags Dialog — filled state (flagCount>0) */}
      {isSuperTenant && flagReport ? (
        <ViewFlagsDialog
          open={viewFlagDialogOpen}
          onOpenChange={setViewFlagDialogOpen}
          creativeHash={creative.hash}
          report={flagReport}
          opportunityId={opportunityId}
        />
      ) : null}
    </div>
  )
}

// Memoized: the virtualizer re-renders the grid on every scroll tick, but each
// `creative` object reference is stable (it comes from the cached infinite-query
// page data), so only cards entering/leaving the window actually re-render. This
// is the primary cut to the scroll-time scripting storm in AD-1569.
export const CreativeCard = memo(CreativeCardImpl)
